import { useState, useEffect, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { Search, Plus, Filter } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu'
import type { Filters } from './types'

interface ContactsToolbarProps {
  count: number
  filters: Filters
  onNewContact: () => void
}

export function ContactsToolbar({ count, filters, onNewContact }: ContactsToolbarProps) {
  const [searchValue, setSearchValue] = useState(filters.search || '')

  // Sync search input with filters prop (e.g. on back navigation)
  useEffect(() => {
    setSearchValue(filters.search || '')
  }, [filters.search])

  const reloadWithFilters = useCallback((newFilters: Record<string, string | undefined>) => {
    router.reload({
      data: {
        search: filters.search || undefined,
        has_email: filters.has_email || undefined,
        has_conversations: filters.has_conversations || undefined,
        ...newFilters,
        page: undefined, // Reset to page 1
      },
      only: ['contacts', 'pagy', 'filters'],
      // contacts is a merge prop for infinite scroll; a filter change must
      // replace the list rather than append to it.
      reset: ['contacts'],
    })
  }, [filters])

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchValue !== (filters.search || '')) {
        reloadWithFilters({ search: searchValue || undefined })
      }
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchValue, filters.search, reloadWithFilters])

  const hasActiveFilters = filters.has_email || filters.has_conversations

  return (
    <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{count.toLocaleString()}</span>
        <span>Contacts</span>
      </div>

      <div className="flex-1" />

      <div className="relative max-w-xs flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search contacts..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="pl-9"
        />
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="cursor-pointer">
            <Filter className="size-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1.5 size-2 rounded-full bg-primary" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel>Email</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={filters.has_email === 'true'}
            onCheckedChange={(checked) =>
              reloadWithFilters({ has_email: checked ? 'true' : undefined })
            }
            className="cursor-pointer"
          >
            Has email
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={filters.has_email === 'false'}
            onCheckedChange={(checked) =>
              reloadWithFilters({ has_email: checked ? 'false' : undefined })
            }
            className="cursor-pointer"
          >
            No email
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Conversations</DropdownMenuLabel>
          <DropdownMenuCheckboxItem
            checked={filters.has_conversations === 'true'}
            onCheckedChange={(checked) =>
              reloadWithFilters({ has_conversations: checked ? 'true' : undefined })
            }
            className="cursor-pointer"
          >
            Has conversations
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={filters.has_conversations === 'false'}
            onCheckedChange={(checked) =>
              reloadWithFilters({ has_conversations: checked ? 'false' : undefined })
            }
            className="cursor-pointer"
          >
            No conversations
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button size="sm" onClick={onNewContact} className="cursor-pointer">
        <Plus className="size-4 mr-2" />
        New Contact
      </Button>
    </div>
  )
}
